import React from "react";
import { useState, useRef } from "react";
import { Nav } from "react-bootstrap";
import styled from "styled-components";
import classNames from "classnames";
import { AutomationPlacement, createDataTestId, AutomationTestIdElementType } from '../../../../utilities/automation';
import NavButton, {NavButtonVariants} from "../../NavButton";

const MenuItemDesktop = ({
    text,
    select,
    onClose,
    className = '',
    item
}) => {
    const [showing, setShowing] = useState(false);

    const closedRef = useRef(null);
    const openRef = useRef(null);

    // Open the menu with a delay (for tabbing through top links)
    function openMenu() {
        if(closedRef.current) {
            clearTimeout(closedRef.current);
        }

        if (!showing) {
            openRef.current = setTimeout(() => {
                select(text, text);
                setShowing(true);
            }, 200)
        } else {
            select(text, text);
            setShowing(true);
        }
    }

    // Close the menu (This starts the timer for delayed closing)
    function closeMenu() {
        if(openRef.current) {
            clearTimeout(openRef.current);
        }

        closedRef.current = setTimeout(() => {
            onClose(text);
            setShowing(false);
            select(null, text);
        }, 200)
    }

    function closeInstant() {
        setShowing(false);
        select(null, text);
    }

    function closeCheck() {
        // Set timeout to wait for the next element to become active
        setTimeout(() => {
            if (!document.querySelector(".nav").contains(document.activeElement)) {
                closeInstant();
            }
        }, 0);
    }

    return (
        <div className={classNames('h-100', className, { 'is-focused' : showing })}>
            <Nav.Link data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.LINK, specifier: text.replace(/[^a-zA-Z]/g, '').toLowerCase()})} href={item.path} className={"link mx-0 text-decoration-none text-uppercase header-link h-100 d-flex align-items-center"} onMouseEnter={openMenu} onMouseLeave={closeMenu} onFocus={openMenu} onBlur={closeCheck}>
                {text}
            </Nav.Link>

            {item.links ?
                <div
                    onMouseEnter={openMenu}
                    onMouseLeave={closeMenu}
                    className={classNames("site-nav")}
                >
                    <div className="container px-0">
                        <div className={classNames("row m-0")}>
                            <div className={classNames("col-3 background-section justify-content-center", item.color)}>
                                <div>
                                    {item.title ? <h4 className="title" dangerouslySetInnerHTML={{ __html: item.title }}></h4> : null}
                                    {item.subtitle ? <h5 className="subtitle" dangerouslySetInnerHTML={{ __html: item.subtitle }}></h5> : null}
                                    {item.body ? <p className="body m-0" dangerouslySetInnerHTML={{ __html: item.body }}></p> : null}
                                    {item.buttonLabel ? <NavButton customClass="mt-4" href={item.buttonUrl} variant={NavButtonVariants.OUTLINE_WHITE} label={item.buttonLabel} /> : null}
                                </div>
                            </div>
                            <div className="col-9 links-section">
                                <div className="row">
                                    {item.links && item.links.map((link, index, links) => (
                                        <div className="col-md" key={text + "" + index}>
                                            {link && link.map(li => (
                                                <div key={li.name} className={(classNames('list-wrapper', {'empty-list': !li.links}))}>
                                                    <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.LINK, specifier: `category_${text.replace(/[^a-zA-Z]/g, '').toLowerCase()}`})} href={li.path} className="d-inline-block text-primary text-uppercase text-decoration-none category-header-link" onBlur={closeCheck}>{li.name}</a>
                                                    {li.links &&
                                                        <ul className="m-0">
                                                            {li.links.map((l, index) => (
                                                                <li key={l.name}>
                                                                    <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.LINK, specifier: `category_li_${text.replace(/[^a-zA-Z]/g, '').toLowerCase()}`})} className="header-text header-link-li" href={l.path} key={index} onBlur={closeCheck}>
                                                                        {l.name}
                                                                    </a>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    }
                                                </div>
                                            ))}
                                            {(item.showSpanish &&  index + 1 === links.length) &&
                                                <div>
                                                    <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.LINK, specifier: `category_${text.replace(/[^a-zA-Z]/g, '').toLowerCase()}`})} href="/es" className="d-inline-block mb-1 text-primary text-uppercase text-decoration-none category-header-link" onBlur={closeCheck}>{'Sitio en Español'}</a>
                                                </div>
                                            }
                                        </div>
                                    ))}
                                </div>
                                {item.footnote ?
                                    <div className="row pt-3 float-right header-text footnote">{item.footnote}</div>
                                    :
                                    null
                                }
                            </div>
                        </div>
                    </div>
                </div>
                :
                null
            }
        </div>
    );
};

export default styled(MenuItemDesktop)<{height?: number | string}>`
  .header-link{
    font-family: "National2";
    font-size: 0.667rem;
    font-weight: 500;
    color: #000000;
    padding-left: 1rem;
    padding-right: 1rem;
    letter-spacing: 0.04rem;

    &:hover {
      color: var(--redRock);
    }

    @media(max-width: 1290px){
        padding-left: .75rem;
        padding-right: .75rem;
    }

    @media(max-width: 1140px){
        padding-left: .5rem;
        padding-right: .5rem;
    }

    @media(max-width: 1090px){
        padding-left: .25rem;
        padding-right: .25rem;
    }
  }

  &.is-focused {
    .site-nav {
        .links-section, .background-section {
            opacity: 1;
            transform: translateY(0);
        }
    }
  }

  .site-nav {
    top: ${props => props.height + "px"};
    .links-section, .background-section {
        opacity: 0;
        transform: translateY(-10px);
        transition: transform .1s,opacity .1s;
        transition-delay: .25s;
    }

    .links-section {
        padding: 2.5rem;

        .list-wrapper {
            margin-bottom: 3.056rem;
            &.empty-list {
                margin-bottom: 1rem;
                .category-header-link {
                    margin-bottom: 0;
                }
            }
            &:last-child {
                margin-bottom: 0;
            }
        }

        @media(max-width: 1200px){
            padding: 2.5rem 1.5rem;
        }
    }

    .background-section {
        background-color: var(--redRock); //Default to redRock if bg class is not applied
        min-height: 400px;
        padding: 5rem 3rem 0;

        .title {
            font-family: "National2";
            font-size: 1.333rem;
            font-weight: 500;
            color: #FFFFFF;
            text-transform: none;
        }
        .subtitle {
            font-family: "National2";
            font-size: 1rem;
            font-weight: 500;
            color: #FFFFFF;
        }
        .body {
            font-family: "National2";
            font-size: 0.889rem;
            font-weight: 300;
            color: #FFFFFF;
        }

        @media(max-width: 1140px){
            padding-left: 2rem;
            padding-right: 2rem;
        }
    }
  }

  .category-header-link {
    font-size: 0.667rem;
    font-weight: 500;
    line-height: 1;
    margin-bottom: 1rem;
    letter-spacing: 0.04rem;

    &:hover {
        color: unset;
        text-decoration: underline !important;
    }
  }

  .header-text {
    font-size: 0.889rem;
    font-weight: 300;
    line-height: 1.5;
  }

  .header-link-li {
    &:hover {
        color: unset;
        text-decoration: underline;
    }
  }

  .footnote {
    font-family: "National2";
    position: absolute;
    bottom: 0.5rem;
    right: 3.5rem;
  }
`;